import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Gauge, Phone, Globe, PhoneCall, Sparkles, Plus, Edit3, Trash2, AlertTriangle, type LucideIcon,
} from 'lucide-react';
import { serviceProfiles, type ServiceProfileItem } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatRate(kbps: number | null): string {
  if (kbps === null) return '—';
  if (kbps >= 1_000_000) return `${(kbps / 1_000_000).toFixed(1)} Gbps`;
  if (kbps >= 1_000) return `${kbps / 1000} Mbps`;
  return `${kbps} kbps`;
}

export const KIND_META: Record<string, { label: string; icon: LucideIcon; color: string; description: string }> = {
  bandwidth:       { label: 'Bandwidth',       icon: Gauge,     color: 'text-c1',    description: 'Profils de vitesse (shaper up/down)' },
  voice:           { label: 'Voice',           icon: Phone,     color: 'text-c5',    description: 'Configuration voix (sip-trunk, codec, dial-plan)' },
  wan_access:      { label: 'WAN Access',      icon: Globe,     color: 'text-c2',    description: 'EVC data (VLAN, DHCP/PPPoE/Static, mac-limit)' },
  voice_transport: { label: 'Voice Transport', icon: PhoneCall, color: 'text-c4',    description: 'EVC voice (VLAN, ip-host)' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Section per kind (extracted for reuse across Data + Voice pages)
// ─────────────────────────────────────────────────────────────────────────────

export function ProfileSection({ kind }: { kind: 'bandwidth' | 'voice' | 'wan_access' | 'voice_transport' }) {
  const [editing, setEditing] = useState<{ profile?: ServiceProfileItem } | null>(null);
  const { data: profiles } = useQuery({
    queryKey: ['service-profiles', kind],
    queryFn: () => serviceProfiles.list(kind),
  });
  const meta = KIND_META[kind];
  const Icon = meta.icon;

  return (
    <>
      <div className="card">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded bg-surface-hi flex items-center justify-center ${meta.color}`}>
              <Icon size={14} />
            </div>
            <div>
              <div className="font-semibold text-sm">{meta.label}</div>
              <div className="text-[11px] text-text-faint">{meta.description}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-text-faint font-mono">{profiles?.length ?? 0} profils</span>
            <button className="btn btn-sm btn-primary" onClick={() => setEditing({})}>
              <Plus size={11} />Nouveau
            </button>
          </div>
        </div>

        {!profiles?.length ? (
          <div className="py-6 text-center text-xs text-text-faint italic">
            Aucun profil — clique "Nouveau" pour en créer un.
          </div>
        ) : (
          <table className="luc-table">
            <thead>
              <tr>
                <th>Nom</th>
                {kind === 'bandwidth' && <th>Up / Down</th>}
                {(kind === 'wan_access' || kind === 'voice_transport') && <><th>VLAN</th><th>Mode</th></>}
                {kind === 'voice' && <th>SIP trunk</th>}
                <th>Vendor refs</th>
                <th>Usage</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <tr key={p.id} className="cursor-pointer" onClick={() => setEditing({ profile: p })}>
                  <td>
                    <div className="font-medium">{p.name}</div>
                    {p.is_parameterized && (
                      <span className="badge badge-info text-[10px] mt-0.5">parameterized</span>
                    )}
                  </td>
                  {kind === 'bandwidth' && (
                    <td className="font-mono">
                      {p.is_parameterized
                        ? <span className="text-text-faint italic">custom (sur sub)</span>
                        : <>{formatRate(p.up_kbps)} <span className="text-text-faint">/</span> {formatRate(p.down_kbps)}</>}
                    </td>
                  )}
                  {(kind === 'wan_access' || kind === 'voice_transport') && (
                    <>
                      <td className="font-mono">{p.vlan ?? '—'}</td>
                      <td className="font-mono text-xs">{p.access_mode ?? '—'}</td>
                    </>
                  )}
                  {kind === 'voice' && (
                    <td className="font-mono text-xs">
                      {(p.vendor_refs as any)?.adtran?.sip_trunk ?? <span className="text-text-faint">—</span>}
                    </td>
                  )}
                  <td className="text-[11px] text-text-faint font-mono truncate max-w-xs">
                    {Object.keys(p.vendor_refs ?? {}).length > 0
                      ? Object.entries(p.vendor_refs).map(([k]) => k).join(', ')
                      : '—'}
                  </td>
                  <td>
                    <UsageBadge profile={p} />
                  </td>
                  <td>
                    <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); setEditing({ profile: p }); }} title="Éditer">
                      <Edit3 size={10} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <ProfileFormModal
          open={!!editing}
          onClose={() => setEditing(null)}
          kind={kind}
          profile={editing.profile}
        />
      )}
    </>
  );
}

function UsageBadge({ profile }: { profile: ServiceProfileItem }) {
  const total = (profile.used_in_bundles ?? 0) + (profile.used_by_voice_subs ?? 0);
  if (total === 0) {
    return <span className="badge text-[10px]">unused</span>;
  }
  const parts: string[] = [];
  if (profile.used_in_bundles) parts.push(`${profile.used_in_bundles} bundle${profile.used_in_bundles > 1 ? 's' : ''}`);
  if (profile.used_by_voice_subs) parts.push(`${profile.used_by_voice_subs} voice sub${profile.used_by_voice_subs > 1 ? 's' : ''}`);
  return (
    <span className="badge badge-brand text-[10px]" title={parts.join(' · ')}>
      {total} ref{total > 1 ? 's' : ''}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Create/Edit modal
// ─────────────────────────────────────────────────────────────────────────────

function ProfileFormModal({ open, onClose, kind, profile }: {
  open: boolean; onClose: () => void;
  kind: 'bandwidth' | 'voice' | 'wan_access' | 'voice_transport';
  profile?: ServiceProfileItem;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = !!profile;
  const [form, setForm] = useState<Partial<ServiceProfileItem>>(() => profile ?? {
    kind, name: '', is_parameterized: false,
    up_kbps: null, down_kbps: null, vlan: null, access_mode: null, mac_limit: null,
    vendor_refs: {},
  });

  const upsert = useMutation({
    mutationFn: () =>
      isEdit ? serviceProfiles.update(profile!.id, form) : serviceProfiles.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-profiles'] });
      qc.invalidateQueries({ queryKey: ['service-bundles'] });
      toast.push({ kind: 'success', title: isEdit ? 'Profil mis à jour' : 'Profil créé' });
      onClose();
    },
    onError: (e: any) => {
      toast.push({ kind: 'error', title: 'Erreur', detail: e?.response?.data?.message ?? e.message });
    },
  });

  const destroy = useMutation({
    mutationFn: () => serviceProfiles.destroy(profile!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-profiles'] });
      toast.push({ kind: 'success', title: 'Profil supprimé' });
      onClose();
    },
    onError: (e: any) => {
      toast.push({ kind: 'error', title: 'Suppression refusée', detail: e?.response?.data?.detail ?? e.message });
    },
  });

  const usageCount = (profile?.used_in_bundles ?? 0) + (profile?.used_by_voice_subs ?? 0);

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? `Éditer ${KIND_META[kind].label} — ${profile!.name}` : `Nouveau ${KIND_META[kind].label}`}>
      <form onSubmit={(e) => { e.preventDefault(); upsert.mutate(); }} className="space-y-3">
        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Nom<span className="text-danger ml-0.5">*</span></span>
          <input
            type="text" required className="input w-full"
            value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={kind === 'bandwidth' ? '200M Sym' : kind === 'voice' ? 'Telkel Residential Voice' : '...'}
          />
        </label>

        {kind === 'bandwidth' && <BandwidthFields form={form} setForm={setForm} />}
        {(kind === 'wan_access' || kind === 'voice_transport') && <WanFields form={form} setForm={setForm} />}
        {kind === 'voice' && <VoiceFields form={form} setForm={setForm} />}

        <div className="flex items-center justify-between pt-3 border-t border-border">
          {isEdit && (
            <button
              type="button"
              className="btn btn-sm text-danger border-danger/30 bg-danger/5 hover:bg-danger/15"
              disabled={usageCount > 0 || destroy.isPending}
              onClick={() => { if (confirm(`Supprimer "${profile!.name}" ?`)) destroy.mutate(); }}
              title={usageCount > 0 ? `Utilisé dans ${usageCount} référence(s) — détache d'abord` : 'Supprimer ce profil'}
            >
              <Trash2 size={10} />{usageCount > 0 ? `Used in ${usageCount}` : 'Supprimer'}
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={onClose} className="btn">Annuler</button>
            <button type="submit" disabled={upsert.isPending} className="btn btn-primary">
              {upsert.isPending ? '...' : (isEdit ? 'Enregistrer' : 'Créer')}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function BandwidthFields({ form, setForm }: { form: Partial<ServiceProfileItem>; setForm: (f: Partial<ServiceProfileItem>) => void }) {
  return (
    <>
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox" checked={!!form.is_parameterized}
          onChange={(e) => setForm({ ...form, is_parameterized: e.target.checked, up_kbps: e.target.checked ? null : form.up_kbps, down_kbps: e.target.checked ? null : form.down_kbps })}
        />
        <span>Parameterized (valeurs sur la subscription — type "Custom")</span>
      </label>
      {!form.is_parameterized && (
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-text-muted block mb-1">Up kbps<span className="text-danger ml-0.5">*</span></span>
            <input
              type="number" required className="input w-full font-mono"
              value={form.up_kbps ?? ''} onChange={(e) => setForm({ ...form, up_kbps: Number(e.target.value) })}
              placeholder="200000"
            />
          </label>
          <label className="block">
            <span className="text-xs text-text-muted block mb-1">Down kbps<span className="text-danger ml-0.5">*</span></span>
            <input
              type="number" required className="input w-full font-mono"
              value={form.down_kbps ?? ''} onChange={(e) => setForm({ ...form, down_kbps: Number(e.target.value) })}
              placeholder="200000"
            />
          </label>
        </div>
      )}
    </>
  );
}

function WanFields({ form, setForm }: { form: Partial<ServiceProfileItem>; setForm: (f: Partial<ServiceProfileItem>) => void }) {
  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs text-text-muted block mb-1">VLAN (1-4094)</span>
          <input
            type="number" min={1} max={4094} className="input w-full font-mono"
            value={form.vlan ?? ''} onChange={(e) => setForm({ ...form, vlan: e.target.value ? Number(e.target.value) : null })}
          />
        </label>
        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Access mode</span>
          <select className="input w-full" value={form.access_mode ?? ''} onChange={(e) => setForm({ ...form, access_mode: e.target.value || null })}>
            <option value="">—</option>
            <option value="dhcp">dhcp</option>
            <option value="dhcp_authenticate">dhcp_authenticate</option>
            <option value="pppoe">pppoe</option>
            <option value="static">static</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-text-muted block mb-1">MAC limit</span>
          <input
            type="number" min={1} className="input w-full font-mono"
            value={form.mac_limit ?? ''} onChange={(e) => setForm({ ...form, mac_limit: e.target.value ? Number(e.target.value) : null })}
          />
        </label>
      </div>
      <VendorRefsField form={form} setForm={setForm} />
    </>
  );
}

function VoiceFields({ form, setForm }: { form: Partial<ServiceProfileItem>; setForm: (f: Partial<ServiceProfileItem>) => void }) {
  const refs = (form.vendor_refs ?? {}) as Record<string, any>;
  const adtran = (refs.adtran ?? {}) as Record<string, any>;
  function setAdtran(field: string, value: any) {
    setForm({ ...form, vendor_refs: { ...refs, adtran: { ...adtran, [field]: value } } });
  }
  return (
    <>
      <div className="text-xs font-semibold text-text-muted">Adtran preset</div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <label className="block">
          <span className="text-text-muted block mb-1">SIP trunk</span>
          <input className="input w-full font-mono" value={adtran.sip_trunk ?? ''} onChange={(e) => setAdtran('sip_trunk', e.target.value)} placeholder="T01" />
        </label>
        <label className="block">
          <span className="text-text-muted block mb-1">Codec list</span>
          <input className="input w-full font-mono" value={adtran.codec_list ?? ''} onChange={(e) => setAdtran('codec_list', e.target.value)} placeholder="defaultCodec" />
        </label>
        <label className="block">
          <span className="text-text-muted block mb-1">Dial plan</span>
          <input className="input w-full font-mono" value={adtran.dial_plan ?? ''} onChange={(e) => setAdtran('dial_plan', e.target.value)} placeholder="default_dial_plan" />
        </label>
        <label className="block">
          <span className="text-text-muted block mb-1">Media profile</span>
          <input className="input w-full font-mono" value={adtran.media_profile ?? ''} onChange={(e) => setAdtran('media_profile', e.target.value)} placeholder="default_media_prof" />
        </label>
        <label className="block">
          <span className="text-text-muted block mb-1">Call feature</span>
          <input className="input w-full font-mono" value={adtran.call_feature ?? ''} onChange={(e) => setAdtran('call_feature', e.target.value)} placeholder="defaultCf" />
        </label>
      </div>
      <div className="text-xs text-text-faint italic flex items-start gap-1.5 pt-1">
        <AlertTriangle size={10} className="text-warning shrink-0 mt-0.5" />
        Ces profils Adtran doivent exister sur l'OLT avant d'assigner une subscription voice. Les SIP credentials (user/pass) sont stockés au niveau du voice_subscription, pas du profil.
      </div>
    </>
  );
}

function VendorRefsField({ form, setForm }: { form: Partial<ServiceProfileItem>; setForm: (f: Partial<ServiceProfileItem>) => void }) {
  const refs = (form.vendor_refs ?? {}) as Record<string, any>;
  const evc = (refs.adtran?.evc ?? '') as string;
  function setAdtranEvc(value: string) {
    setForm({ ...form, vendor_refs: { ...refs, adtran: { ...(refs.adtran ?? {}), evc: value } } });
  }
  return (
    <label className="block">
      <span className="text-xs text-text-muted block mb-1">Adtran EVC name (référence pré-existante sur l'OLT)</span>
      <input className="input w-full font-mono" value={evc} onChange={(e) => setAdtranEvc(e.target.value)} placeholder="VLAN_100_DATA" />
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bundle section — filtered by which profile kinds a bundle contains
// ─────────────────────────────────────────────────────────────────────────────

export function BundleSection({ filter }: { filter: 'data' | 'voice' }) {
  const { data: bundles } = useQuery({
    queryKey: ['service-bundles'],
    queryFn: () => serviceProfiles.bundles(),
  });

  // Filter : data bundles = include bandwidth or wan_access ; voice bundles = include voice or voice_transport
  const filtered = (bundles ?? []).filter((b) => {
    const kinds = new Set(b.profiles.map((p) => p.kind));
    if (filter === 'data') return kinds.has('bandwidth') || kinds.has('wan_access');
    return kinds.has('voice') || kinds.has('voice_transport');
  });

  return (
    <div className="card">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded bg-surface-hi flex items-center justify-center text-brand">
            <Sparkles size={14} />
          </div>
          <div>
            <div className="font-semibold text-sm">Bundles {filter === 'data' ? 'Data' : 'Voix'}</div>
            <div className="text-[11px] text-text-faint">
              {filter === 'data' ? 'Compositions data (ex: Résidentiel 1G)' : 'Compositions voix (ex: Voice Telkel)'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-text-faint font-mono">{filtered.length} bundles</span>
          <button className="btn btn-sm btn-primary" disabled title="Création de bundle (à venir)">
            <Plus size={11} />Nouveau bundle
          </button>
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="py-6 text-center text-xs text-text-faint italic">
          Aucun bundle créé pour cette catégorie.
        </div>
      ) : (
        <table className="luc-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Profils inclus</th>
              <th>Subscriptions actives</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr key={b.id}>
                <td>
                  <div className="font-medium">{b.name}</div>
                  {b.description && <div className="text-[11px] text-text-faint">{b.description}</div>}
                </td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {b.profiles.map((p) => (
                      <span key={p.id} className="badge badge-info text-[10px]" title={p.kind}>
                        {p.name}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <span className="font-mono text-text-muted">{b.active_subscriptions ?? 0}</span>
                </td>
                <td>
                  {b.is_active
                    ? <span className="badge badge-up text-[10px]">active</span>
                    : <span className="badge text-[10px]">inactive</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
